import React from 'react'

import {
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Box,
    Chip,
    Grid,
    ListItemText,
} from '@material-ui/core'
import { Mail, Message as MessageSquare } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { CampaignData, CTAOption, SequenceItem, SequenceStep } from './types'
import { useConcessions } from '../../../../hooks/useConcessions'

const useStyles = makeStyles((theme: Theme) => ({
    cardTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    cardDescription: {
        color: '#6b7280',
        fontSize: '0.875rem',
        marginTop: theme.spacing(0.5),
    },
    formField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover fieldset': {
                borderColor: '#3b82f6',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: 500,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#3b82f6',
        },
    },
    channelCard: {
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
    },
    summaryCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    summaryItem: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    progressItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    progressCheck: {
        color: '#10b981',
        fontWeight: 'bold',
    },
    progressCircle: {
        color: '#9ca3af',
    },
    draftWarning: {
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(1),
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(2),
    },
    gradientButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
}))

interface CampaignSetupTabProps {
    campaignData: CampaignData
    setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>
    ctaOptions: CTAOption[]
    sequence: SequenceItem[]
    onNext: () => void
    /** When set, channel checkboxes are hidden and channels are fixed (primary campaigns). */
    lockChannels?: boolean
    readOnlyName?: boolean
    continueLabel?: string
    showDraftReplyWarning?: boolean
}

const isMessageStep = (item: SequenceItem): item is SequenceStep => {
    return item.type === 'email' || item.type === 'sms'
}

export const CampaignSetupTab: React.FC<CampaignSetupTabProps> = ({
    campaignData,
    setCampaignData,
    ctaOptions,
    sequence,
    onNext,
    lockChannels = false,
    readOnlyName = false,
    continueLabel = 'Continue to Sequence →',
    showDraftReplyWarning = false,
}) => {
    const classes = useStyles()
    const { concessions } = useConcessions()
    const messageSteps = sequence.filter(isMessageStep)
    const channelsValid =
        lockChannels || campaignData.channels.email || campaignData.channels.sms
    const showDraftWarning =
        showDraftReplyWarning &&
        campaignData.reply_to_email.trim().endsWith('@ezmanage.local')

    return (
        <Grid container spacing={3}>
            {/* Campaign Setup */}
            <Grid item xs={12} lg={8}>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24,
                    }}
                >
                    {/* Basic Information */}
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                Campaign Details
                            </Typography>
                            <Typography className={classes.cardDescription}>
                                Basic information about your campaign
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 16,
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label="Campaign Name"
                                    placeholder="e.g., Summer Graduate Outreach 2024"
                                    value={campaignData.name}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCampaignData({
                                            ...campaignData,
                                            name: e.target.value,
                                        })
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                    required
                                    disabled={readOnlyName}
                                />
                                <TextField
                                    fullWidth
                                    label="Reply-To Email"
                                    placeholder="e.g., leasing@oakwoodapartments.com"
                                    value={campaignData.reply_to_email}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCampaignData({
                                            ...campaignData,
                                            reply_to_email: e.target.value,
                                        })
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                    type="email"
                                    required
                                />
                                {showDraftWarning && (
                                    <Box className={classes.draftWarning}>
                                        Reply-to is still a placeholder. Enter
                                        your property&apos;s real leasing email
                                        before activating.
                                    </Box>
                                )}
                                <TextField
                                    fullWidth
                                    label="Sender Name"
                                    placeholder="e.g., Sarah from Oakwood Apartments"
                                    value={campaignData.sender_name}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCampaignData({
                                            ...campaignData,
                                            sender_name: e.target.value,
                                        })
                                    }
                                    className={classes.formField}
                                    variant="outlined"
                                    required
                                />
                                <FormControl
                                    variant="outlined"
                                    fullWidth
                                    className={classes.formField}
                                >
                                    <InputLabel
                                        id="campaign-concession-multi-label"
                                        shrink={
                                            (campaignData.concessions ?? [])
                                                .length > 0
                                        }
                                        style={
                                            (campaignData.concessions ?? [])
                                                .length === 0
                                                ? {
                                                      transform:
                                                          'translate(14px, 12px) scale(1)',
                                                  }
                                                : undefined
                                        }
                                    >
                                        Concessions (optional)
                                    </InputLabel>
                                    <Select
                                        labelId="campaign-concession-multi-label"
                                        multiple
                                        value={campaignData.concessions ?? []}
                                        onChange={(e) =>
                                            setCampaignData({
                                                ...campaignData,
                                                concessions: e.target
                                                    .value as number[],
                                            })
                                        }
                                        label={
                                            (campaignData.concessions ?? [])
                                                .length > 0
                                                ? 'Concessions (optional)'
                                                : undefined
                                        }
                                        renderValue={(selected) => {
                                            const ids = selected as number[]
                                            if (ids.length === 0) return <></>
                                            return (
                                                <Box
                                                    display="flex"
                                                    flexWrap="nowrap"
                                                    alignItems="center"
                                                    style={{
                                                        gap: 4,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {ids.map((id) => {
                                                        const c = concessions.find(
                                                            (x) => x.id === id,
                                                        )
                                                        return (
                                                            <Chip
                                                                key={id}
                                                                label={
                                                                    c?.name ??
                                                                    id
                                                                }
                                                                size="small"
                                                                style={{
                                                                    height: 20,
                                                                    fontSize:
                                                                        '0.675rem',
                                                                    fontWeight: 600,
                                                                    backgroundColor:
                                                                        '#d1fae5',
                                                                    color:
                                                                        '#047857',
                                                                    borderRadius: 6,
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        )
                                                    })}
                                                </Box>
                                            )
                                        }}
                                        style={{ maxHeight: 40 }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                    marginTop: 4,
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            getContentAnchorEl: null,
                                        }}
                                    >
                                        {concessions
                                            .filter((c) => c.is_active)
                                            .map((c) => (
                                                <MenuItem
                                                    key={c.id}
                                                    value={c.id}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={(
                                                            campaignData.concessions ??
                                                            []
                                                        ).includes(c.id)}
                                                        size="small"
                                                        style={{
                                                            color: '#047857',
                                                            padding:
                                                                '0 8px 0 0',
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={c.name}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            style: {
                                                                fontWeight: 500,
                                                            },
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Channel Selection */}
                    {lockChannels ? (
                        <Card className={classes.summaryCard}>
                            <Box style={{ padding: '24px 24px 16px 24px' }}>
                                <Typography className={classes.cardTitle}>
                                    Communication Channel
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    Fixed for this system primary campaign
                                </Typography>
                            </Box>
                            <CardContent
                                style={{ padding: '0 24px 24px 24px' }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 12 }}
                                >
                                    {campaignData.channels.email ? (
                                        <Mail
                                            style={{
                                                color: '#3b82f6',
                                                fontSize: 24,
                                            }}
                                        />
                                    ) : (
                                        <MessageSquare
                                            style={{
                                                color: '#8b5cf6',
                                                fontSize: 24,
                                            }}
                                        />
                                    )}
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            style={{ fontWeight: 600 }}
                                        >
                                            {campaignData.channels.email
                                                ? 'Email'
                                                : 'SMS'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {campaignData.channels.email
                                                ? 'Multi-step email sequence'
                                                : 'Text message follow-ups'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className={classes.summaryCard}>
                            <Box style={{ padding: '24px 24px 16px 24px' }}>
                                <Typography className={classes.cardTitle}>
                                    Communication Channels
                                </Typography>
                                <Typography className={classes.cardDescription}>
                                    Choose how to reach your prospects (at least
                                    one required)
                                </Typography>
                            </Box>
                            <CardContent
                                style={{ padding: '0 24px 24px 24px' }}
                            >
                                {!campaignData.channels.email &&
                                    !campaignData.channels.sms && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            style={{
                                                display: 'block',
                                                marginBottom: 16,
                                            }}
                                        >
                                            Please select at least one
                                            communication channel
                                        </Typography>
                                    )}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            className={classes.channelCard}
                                            onClick={() =>
                                                setCampaignData({
                                                    ...campaignData,
                                                    channels: {
                                                        ...campaignData.channels,
                                                        email: !campaignData
                                                            .channels.email,
                                                    },
                                                })
                                            }
                                        >
                                            <Checkbox
                                                checked={
                                                    campaignData.channels.email
                                                }
                                                onChange={(e) =>
                                                    setCampaignData({
                                                        ...campaignData,
                                                        channels: {
                                                            ...campaignData.channels,
                                                            email:
                                                                e.target
                                                                    .checked,
                                                        },
                                                    })
                                                }
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 8 }}
                                            >
                                                <Mail
                                                    style={{
                                                        color: '#3b82f6',
                                                        fontSize: 20,
                                                    }}
                                                />
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Email
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Multi-step email
                                                        sequences
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box
                                            className={classes.channelCard}
                                            onClick={() =>
                                                setCampaignData({
                                                    ...campaignData,
                                                    channels: {
                                                        ...campaignData.channels,
                                                        sms: !campaignData
                                                            .channels.sms,
                                                    },
                                                })
                                            }
                                        >
                                            <Checkbox
                                                checked={
                                                    campaignData.channels.sms
                                                }
                                                onChange={(e) =>
                                                    setCampaignData({
                                                        ...campaignData,
                                                        channels: {
                                                            ...campaignData.channels,
                                                            sms:
                                                                e.target
                                                                    .checked,
                                                        },
                                                    })
                                                }
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 8 }}
                                            >
                                                <MessageSquare
                                                    style={{
                                                        color: '#8b5cf6',
                                                        fontSize: 20,
                                                    }}
                                                />
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        SMS
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Text message follow-ups
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* CTA Selection */}
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography className={classes.cardTitle}>
                                Campaign Goal
                            </Typography>
                            <Typography className={classes.cardDescription}>
                                What action do you want prospects to take?
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <FormControl
                                fullWidth
                                className={classes.formField}
                                variant="outlined"
                            >
                                <InputLabel>Call-to-Action</InputLabel>
                                <Select
                                    value={campaignData.cta}
                                    onChange={(
                                        e: React.ChangeEvent<{
                                            value: unknown
                                        }>,
                                    ) =>
                                        setCampaignData({
                                            ...campaignData,
                                            cta: e.target.value as string,
                                        })
                                    }
                                    label="Call-to-Action"
                                >
                                    {ctaOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Box>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24,
                    }}
                >
                    {/* Campaign Summary */}
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography
                                className={classes.cardTitle}
                                style={{ fontSize: '1.125rem' }}
                            >
                                Campaign Summary
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                }}
                            >
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Name:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {campaignData.name || 'Untitled'}
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Channels:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {
                                            Object.values(
                                                campaignData.channels,
                                            ).filter(Boolean).length
                                        }
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Messages:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {messageSteps.length}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Progress */}
                    <Card className={classes.summaryCard}>
                        <Box style={{ padding: '24px 24px 16px 24px' }}>
                            <Typography
                                className={classes.cardTitle}
                                style={{ fontSize: '1.125rem' }}
                            >
                                Setup Progress
                            </Typography>
                        </Box>
                        <CardContent style={{ padding: '0 24px 24px 24px' }}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                }}
                            >
                                <Box className={classes.progressItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Campaign Details
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={
                                            campaignData.name &&
                                            campaignData.reply_to_email &&
                                            campaignData.sender_name
                                                ? classes.progressCheck
                                                : classes.progressCircle
                                        }
                                    >
                                        {campaignData.name &&
                                        campaignData.reply_to_email &&
                                        campaignData.sender_name
                                            ? '✓'
                                            : '○'}
                                    </Typography>
                                </Box>
                                <Box className={classes.progressItem}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Message Sequence
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={
                                            messageSteps.length > 0
                                                ? classes.progressCheck
                                                : classes.progressCircle
                                        }
                                    >
                                        {messageSteps.length > 0 ? '✓' : '○'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                        }}
                    >
                        <Button
                            onClick={onNext}
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={
                                !campaignData.name ||
                                !campaignData.reply_to_email ||
                                !campaignData.sender_name ||
                                !channelsValid
                            }
                            className={classes.gradientButton}
                        >
                            {continueLabel}
                        </Button>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    )
}
